import { Check, Info } from 'lucide-react';
import { useState } from 'react';

export default function PlanSelection() {
    const [billingCycle, setBillingCycle] = useState('yearly');
    const [selectedPlan, setSelectedPlan] = useState('free');

    const plans = [
        {
            id: 'free',
            name: 'Free',
            price: { monthly: 0, yearly: 0 },
            description: 'Pour démarrer et explorer les fonctionnalités de base',
            features: [
                'Accès à 3 projets',
                '500 requêtes/mois',
                'Exportation PDF basique',
                'Support par email'
            ],
            cta: 'Plan actuel',
            disabled: selectedPlan === 'free',
            highlight: false
        },
        {
            id: 'pro',
            name: 'Pro',
            price: { monthly: 29, yearly: 24 },
            description: 'Pour les professionnels qui ont besoin de plus de puissance',
            features: [
                'Accès à 15 projets',
                '5 000 requêtes/mois',
                'Exportation avancée (PDF, Excel)',
                'Support prioritaire',
                'Intégrations API',
                'Tableaux de bord personnalisés'
            ],
            cta: 'Passer au Pro',
            disabled: selectedPlan === 'pro',
            highlight: true
        },
        {
            id: 'enterprise',
            name: 'Enterprise',
            price: { monthly: 79, yearly: 69 },
            description: 'Pour les équipes et les besoins avancés',
            features: [
                'Projets illimités',
                'Requêtes illimitées',
                'Exportations avancées',
                'Support dédié 24/7',
                'Intégrations API avancées',
                'SSO & sécurité renforcée',
                'Formations personnalisées',
                'SLA garantie'
            ],
            cta: 'Passer à Enterprise',
            disabled: selectedPlan === 'enterprise',
            highlight: false
        }
    ];

    const handleBillingCycleChange = (cycle) => {
        setBillingCycle(cycle);
    };

    const handlePlanSelect = (planId) => {
        if (planId !== selectedPlan) {
            setSelectedPlan(planId);
            // Ici vous mettriez la logique pour gérer le changement de plan
        }
    };

    return (
        <div className="max-w-6xl mx-auto py-8 px-4">
            <div className="text-center mb-12">
                <h1 className="text-2xl font-bold text-gray-900 mb-3">Choisissez le plan adapté à vos besoins</h1>
                <p className="text-gray-600 max-w-2xl mx-auto">
                    Tous nos plans incluent une garantie satisfait ou remboursé de 14 jours. Vous pouvez changer ou annuler votre plan à tout moment.
                </p>

                {/* Sélecteur de cycle de facturation */}
                <div className="mt-8 inline-flex items-center bg-gray-100 p-1 rounded-lg">
                    <button
                        onClick={() => handleBillingCycleChange('monthly')}
                        className={`px-4 py-2 text-sm font-medium rounded-md ${billingCycle === 'monthly'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        Mensuel
                    </button>
                    <button
                        onClick={() => handleBillingCycleChange('yearly')}
                        className={`px-4 py-2 text-sm font-medium rounded-md flex items-center ${billingCycle === 'yearly'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                            }`}
                    >
                        Annuel
                        <span className="ml-2 bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                            -20%
                        </span>
                    </button>
                </div>
            </div>

            {/* Cartes des plans */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {plans.map((plan) => (
                    <div
                        key={plan.id}
                        className={`relative bg-white rounded-xl overflow-hidden border ${plan.highlight
                            ? 'border-[#1D7C6E] shadow-lg'
                            : 'border-gray-200 shadow'
                            }`}
                    >
                        {plan.highlight && (
                            <div className="absolute top-0 right-0 left-0 bg-[#1D7C6E] text-white text-center text-xs font-medium py-1">
                                Recommandé
                            </div>
                        )}

                        <div className={`p-6 ${plan.highlight ? 'pt-8' : ''}`}>
                            <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                            <p className="mt-1 text-sm text-gray-500 h-10">{plan.description}</p>

                            <div className="mt-4 flex items-baseline">
                                <span className="text-3xl font-bold text-gray-900">
                                    {plan.price[billingCycle] === 0 ? 'Gratuit' : `${plan.price[billingCycle]} €`}
                                </span>
                                {plan.price[billingCycle] > 0 && (
                                    <span className="ml-1 text-sm text-gray-500">
                                        /{billingCycle === 'monthly' ? 'mois' : 'mois, facturé annuellement'}
                                    </span>
                                )}
                            </div>

                            <button
                                onClick={() => handlePlanSelect(plan.id)}
                                disabled={plan.disabled}
                                className={`mt-6 w-full rounded-md py-2 px-4 text-sm font-medium ${plan.disabled
                                    ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
                                    : plan.highlight
                                        ? 'bg-[#1D7C6E] text-white hover:bg-[#176158] transition-colors'
                                        : 'bg-white text-[#1D7C6E] border border-[#1D7C6E] hover:bg-[#E6F2EF] transition-colors'
                                    }`}
                            >
                                {plan.disabled ? plan.cta : plan.cta}
                            </button>

                            <ul className="mt-6 space-y-3">
                                {plan.features.map((feature, index) => (
                                    <li key={index} className="flex items-start">
                                        <Check size={16} className="text-[#1D7C6E] mt-0.5 mr-2 flex-shrink-0" />
                                        <span className="text-sm text-gray-600">{feature}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                ))}
            </div>

            {/* Section FAQ */}
            <div className="mt-16 bg-gray-50 rounded-lg p-6">
                <div className="flex items-start mb-4">
                    <Info size={20} className="text-[#1D7C6E] mr-2 mt-1 flex-shrink-0" />
                    <div>
                        <h3 className="font-medium text-gray-900">Besoin d'une solution sur mesure ?</h3>
                        <p className="mt-1 text-sm text-gray-600">
                            Nous proposons également des plans personnalisés pour les grandes organisations avec des besoins spécifiques.
                            <a href="#" className="ml-1 text-[#1D7C6E] hover:underline">Contactez notre équipe commerciale</a>.
                        </p>
                    </div>
                </div>
            </div>

            {/* Section de garanties */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div>
                    <div className="mb-2 flex justify-center">
                        <svg className="h-8 w-8 text-[#1D7C6E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                    </div>
                    <h4 className="text-sm font-medium text-gray-900">Paiement sécurisé</h4>
                    <p className="mt-1 text-xs text-gray-500">Vos données de paiement sont protégées et sécurisées.</p>
                </div>
                <div>
                    <div className="mb-2 flex justify-center">
                        <svg className="h-8 w-8 text-[#1D7C6E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                    </div>
                    <h4 className="text-sm font-medium text-gray-900">Support client dédié</h4>
                    <p className="mt-1 text-xs text-gray-500">Notre équipe est disponible pour vous aider à tout moment.</p>
                </div>
                <div>
                    <div className="mb-2 flex justify-center">
                        <svg className="h-8 w-8 text-[#1D7C6E]" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    <h4 className="text-sm font-medium text-gray-900">Satisfaction garantie</h4>
                    <p className="mt-1 text-xs text-gray-500">Remboursement sous 14 jours si vous n'êtes pas satisfait.</p>
                </div>
            </div>
        </div>
    );
}